import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import { RefreshCw, Zap, Mail, Cloud } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import type { CustomJourney } from "@shared/schema";

type JourneyResponse = {
  success: boolean;
  data: (CustomJourney & { steps?: any[] }) | null;
  message?: string;
};

type TriggerEventOption = {
  id: string;
  name: string;
  description: string;
  category: string;
  parameters?: string[];
};

type SesTemplate = {
  templateName: string;
  createdTimestamp?: string;
};

type SmsTemplate = {
  id: number;
  name: string;
  templateKey: string;
  category: string;
};

interface StepConfigurationProps {
  journeyId?: number | null;
  onStepUpdated?: () => void;
}

function sanitizeNumericInput(value: string): string {
  return value.replace(/[^0-9]/g, "");
}

export default function StepConfiguration({ journeyId, onStepUpdated }: StepConfigurationProps) {
  const { toast } = useToast();
  const [selectedTrigger, setSelectedTrigger] = useState<string>("");
  const [selectedTemplate, setSelectedTemplate] = useState<string>("");
  const [sendOnWeekends, setSendOnWeekends] = useState(true);
  const [rateLimit, setRateLimit] = useState<string>("");
  const [deliveryChannel, setDeliveryChannel] = useState<"email" | "sms" | "in_app">("email");
  const [smsTemplateId, setSmsTemplateId] = useState<string>("");

  const {
    data: journeyData,
    isLoading: journeyLoading,
    isError: journeyError,
    refetch: refetchJourney,
  } = useQuery<JourneyResponse>({
    queryKey: ["/api/admin/journey-builder/journeys", journeyId],
    enabled: Boolean(journeyId),
    queryFn: async () => {
      const response = await apiRequest("GET", `/api/admin/journey-builder/journeys/${journeyId}`);
      return response.json();
    },
  });

  const journey = journeyData?.data ?? null;

  const {
    data: triggerEvents = [],
    refetch: refetchTriggerEvents,
  } = useQuery<TriggerEventOption[]>({
    queryKey: ["/api/admin/journey-builder/trigger-events"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/journey-builder/trigger-events");
      return response.json();
    },
  });

  const {
    data: sesTemplatesResponse,
    isLoading: sesTemplatesLoading,
    isError: sesTemplatesError,
    error: sesTemplatesErrorObject,
    refetch: refetchSesTemplates,
  } = useQuery<{ templates?: SesTemplate[] }>({
    queryKey: ["/api/admin/crm/ses-templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/admin/crm/ses-templates");
      return response.json();
    },
  });

  const sesTemplates = sesTemplatesResponse?.templates ?? [];
  const sesErrorMessage = sesTemplatesError
    ? sesTemplatesErrorObject instanceof Error
      ? sesTemplatesErrorObject.message
      : "Failed to load SES templates"
    : undefined;

  const { data: smsTemplates = [], isLoading: smsTemplatesLoading } = useQuery<SmsTemplate[]>({
    queryKey: ["/api/sms-config/templates"],
    queryFn: async () => {
      const response = await apiRequest("GET", "/api/sms-config/templates");
      return response.json();
    },
  });

  useEffect(() => {
    if (!journey) return;
    setSelectedTrigger(journey.triggerEvent || "");
    setSelectedTemplate(journey.sesTemplateName || "");

    const scheduling = (journey.schedulingSettings as any) || {};
    setSendOnWeekends(scheduling.sendOnWeekends !== false);
    setRateLimit(
      typeof scheduling.rateLimit === "number" && !Number.isNaN(scheduling.rateLimit)
        ? String(scheduling.rateLimit)
        : "",
    );

    const currentSettings = (journey.settings as any) || {};
    const selectedChannel =
      currentSettings.deliveryChannel || currentSettings.channel || (journey.config as any)?.channel;
    if (selectedChannel === "sms" || selectedChannel === "in_app") {
      setDeliveryChannel(selectedChannel);
    } else {
      setDeliveryChannel("email");
    }
    if (currentSettings.smsTemplateId) {
      setSmsTemplateId(String(currentSettings.smsTemplateId));
    } else {
      setSmsTemplateId("");
    }
  }, [
    journey?.id,
    journey?.triggerEvent,
    journey?.sesTemplateName,
    JSON.stringify(journey?.schedulingSettings ?? {}),
    JSON.stringify(journey?.settings ?? {}),
    JSON.stringify(journey?.config ?? {}),
  ]);

  const updateJourneyMutation = useMutation({
    mutationFn: async () => {
      if (!journeyId) {
        throw new Error("Select a journey to update its configuration.");
      }

      const schedulingSettings: Record<string, any> = {
        ...(journey?.schedulingSettings as any),
        sendOnWeekends,
        sendImmediately: true,
      };
      if (rateLimit) {
        schedulingSettings.rateLimit = Number(rateLimit);
      } else {
        delete schedulingSettings.rateLimit;
      }

      const currentSettings = (journey?.settings as any) || {};

      const updatedSettings: Record<string, any> = {
        ...currentSettings,
        executionMode: "immediate",
        stepMode: "single-send",
        deliveryChannel,
      };

      if (deliveryChannel === "sms" || deliveryChannel === "in_app") {
        if (smsTemplateId) {
          updatedSettings.smsTemplateId = Number(smsTemplateId);
        } else {
          delete updatedSettings.smsTemplateId;
        }
      } else {
        delete updatedSettings.smsTemplateId;
      }

      const payload: Record<string, any> = {
        schedulingSettings,
        settings: updatedSettings,
      };

      if (selectedTrigger) {
        payload.triggerEvent = selectedTrigger;
      }

      payload.sesTemplateName = selectedTemplate || null;

      const response = await apiRequest("PUT", `/api/admin/journey-builder/journeys/${journeyId}`, {
        headers: { "Content-Type": "application/json" },
        body: payload,
      });
      return response.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Journey configuration saved",
        description: data?.message || "Journey updated successfully.",
      });
      refetchJourney();
      onStepUpdated?.();
    },
    onError: (error: any) => {
      toast({
        title: "Unable to save configuration",
        description: error?.message || "Failed to update journey settings.",
        variant: "destructive",
      });
    },
  });

  const toggleStatusMutation = useMutation({
    mutationFn: async (nextStatus: "active" | "paused") => {
      if (!journeyId) {
        throw new Error("Select a journey first.");
      }
      const response = await apiRequest(
        "POST",
        `/api/admin/journey-builder/journeys/${journeyId}/activate`,
        {
          headers: { "Content-Type": "application/json" },
          body: {
            isActive: nextStatus === "active",
          },
        },
      );
      return response.json();
    },
    onSuccess: (data, variables) => {
      toast({
        title: variables === "active" ? "Journey activated" : "Journey paused",
        description: data?.message || "Status updated successfully.",
      });
      refetchJourney();
      onStepUpdated?.();
    },
    onError: (error: any) => {
      toast({
        title: "Unable to update status",
        description: error?.message || "Failed to toggle journey status.",
        variant: "destructive",
      });
    },
  });

  const handleSave = () => {
    updateJourneyMutation.mutate();
  };

  const handleRefresh = () => {
    refetchJourney();
    refetchSesTemplates();
    refetchTriggerEvents();
    toast({ title: "Configuration refreshed" });
  };

  if (!journeyId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Configure Journey</CardTitle>
          <CardDescription>
            Select a journey from the list to configure its immediate send behaviour.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Journeys trigger instantly on their configured event. Choose a journey to map it to the correct trigger and
          SES template.
        </CardContent>
      </Card>
    );
  }

  if (journeyLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-16 text-sm text-muted-foreground">
          <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
          Loading journey configuration...
        </CardContent>
      </Card>
    );
  }

  if (journeyError || !journey) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Unable to load journey</CardTitle>
          <CardDescription>We could not retrieve the journey details. Try reloading.</CardDescription>
        </CardHeader>
        <CardContent>
          <Button onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const currentTriggerLabel = (() => {
    if (!selectedTrigger) return "No trigger selected";
    const match = triggerEvents.find((event) => event.id === selectedTrigger);
    return match?.name || selectedTrigger;
  })();

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Zap className="h-5 w-5" />
          Immediate Journey Dispatch
          <Badge variant={journey.status === "active" ? "default" : "outline"}>{journey.status}</Badge>
        </CardTitle>
        <CardDescription>
          Journeys no longer use multi-step flows. Configure the trigger and email template that should fire
          immediately when the event occurs.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-4">
          <div className="space-y-1">
            <Label>Journey</Label>
            <div className="rounded-lg border bg-muted/40 p-3 text-sm">
              <div className="font-medium">{journey.name}</div>
              <div className="text-muted-foreground">{journey.description || "No description provided."}</div>
            </div>
          </div>
          <div className="space-y-1">
            <Label>Trigger Event</Label>
            <Select value={selectedTrigger} onValueChange={setSelectedTrigger}>
              <SelectTrigger>
                <SelectValue placeholder="Select trigger" />
              </SelectTrigger>
              <SelectContent>
                {triggerEvents.map((event) => (
                  <SelectItem key={event.id} value={event.id}>
                    {event.name || event.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              The journey fires as soon as this CRM event is emitted.
            </p>
          </div>
          <div className="space-y-1">
            <Label>SES Template</Label>
            <Select
              value={selectedTemplate}
              onValueChange={setSelectedTemplate}
              disabled={sesTemplatesLoading || (!!sesErrorMessage && !selectedTemplate)}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={
                    sesTemplatesLoading
                      ? "Loading templates..."
                      : sesErrorMessage
                        ? "Failed to load templates"
                        : sesTemplates.length
                          ? "Choose SES template"
                          : "No templates found"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {sesTemplates.map((template) => (
                  <SelectItem key={template.templateName} value={template.templateName}>
                    {template.templateName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 pt-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => refetchSesTemplates()}
                disabled={sesTemplatesLoading}
              >
                <RefreshCw className="mr-1 h-4 w-4" />
                Refresh
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedTemplate("")} disabled={!selectedTemplate}>
                Clear
              </Button>
            </div>
            {selectedTemplate && (
              <p className="text-xs text-muted-foreground">Selected template: {selectedTemplate}</p>
            )}
            {sesErrorMessage ? (
              <p className="text-xs text-destructive pt-2">SES templates unavailable: {sesErrorMessage}</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label>Delivery Channel</Label>
            <Select
              value={deliveryChannel}
              onValueChange={(value) => setDeliveryChannel(value as "email" | "sms" | "in_app")}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
                <SelectItem value="in_app">In-App</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Choose whether this journey sends an email, SMS, or in-app notification when triggered.
            </p>
            {(deliveryChannel === "sms" || deliveryChannel === "in_app") && (
              <div className="mt-3 space-y-1">
                <Label>{deliveryChannel === "sms" ? "SMS Template" : "In-App Template (uses SMS content)"}</Label>
                <Select
                  value={smsTemplateId}
                  onValueChange={setSmsTemplateId}
                  disabled={smsTemplatesLoading || smsTemplates.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        smsTemplatesLoading
                          ? "Loading SMS templates..."
                          : smsTemplates.length
                            ? "Choose SMS template"
                            : "No SMS templates found"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {smsTemplates.map((template) => (
                      <SelectItem key={template.id} value={template.id.toString()}>
                        {template.name} ({template.templateKey})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  {deliveryChannel === "sms"
                    ? 'SMS content comes from this template using {{variable}} placeholders.'
                    : 'In-app message text is generated from this SMS template using {{variable}} placeholders.'}
                </p>
              </div>
            )}
          </div>
        </div>

        <Separator />

        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Send Controls
            </Label>
            <div className="mt-2 space-y-3 rounded-lg border bg-muted/40 p-3 text-sm">
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium">Send on weekends</div>
                  <p className="text-xs text-muted-foreground">
                    Disable if the journey should pause on Saturdays and Sundays.
                  </p>
                </div>
                <Switch checked={sendOnWeekends} onCheckedChange={setSendOnWeekends} />
              </div>
              <div>
                <Label htmlFor="journey-rate-limit" className="text-xs text-muted-foreground">
                  Optional rate limit (emails per hour)
                </Label>
                <Input
                  id="journey-rate-limit"
                  type="number"
                  min={0}
                  placeholder="Unlimited"
                  value={rateLimit}
                  onChange={(event) => setRateLimit(sanitizeNumericInput(event.target.value))}
                  className="mt-2"
                />
              </div>
            </div>
          </div>
          <div>
            <Label className="flex items-center gap-2">
              <Cloud className="h-4 w-4" />
              Current Configuration
            </Label>
              <div className="mt-2 space-y-2 rounded-lg border bg-muted/40 p-3 text-sm text-muted-foreground">
              <div>
                <strong>Trigger:</strong> {currentTriggerLabel}
              </div>
              <div>
                <strong>Template:</strong>{" "}
                {deliveryChannel === "sms" || deliveryChannel === "in_app"
                  ? smsTemplates.find((t) => t.id.toString() === smsTemplateId)?.name || "SMS template not set"
                  : selectedTemplate || "Not set"}
              </div>
              <div>
                <strong>Weekend sending:</strong> {sendOnWeekends ? "Enabled" : "Disabled"}
              </div>
              <div>
                <strong>Rate limit:</strong> {rateLimit ? `${rateLimit}/hour` : "Unlimited"}
              </div>
              <div>
                <strong>Execution:</strong> Immediate on event emission
              </div>
            </div>
          </div>
        </div>

        <Separator />

        <div className="flex flex-wrap gap-2">
          <Button onClick={handleSave} disabled={updateJourneyMutation.isPending}>
            Save Configuration
          </Button>
          <Button variant="outline" onClick={handleRefresh}>
            <RefreshCw className="mr-1 h-4 w-4" />
            Refresh
          </Button>
          <Button
            variant={journey.status === "active" ? "destructive" : "secondary"}
            onClick={() => toggleStatusMutation.mutate(journey.status === "active" ? "paused" : "active")}
            disabled={toggleStatusMutation.isPending}
          >
            {journey.status === "active" ? "Pause Journey" : "Activate Journey"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
